import { useState } from "react";
import { Popover as AriaPopover, Dialog, DialogTrigger, type Key } from "react-aria-components";
import { useTranslation } from "react-i18next";
import useMeasure from "react-use-measure";

import { CloseIcon } from "@/assets/icons/general/Close";
import { InputHeader } from "@/components/ui/inputs/shared/InputHeader";
import { Drawer } from "@/components/ui/overlays/Drawer/Drawer";

import type { DropdownProps } from "./Dropdown";
import { DropdownButton } from "./DropdownButton";
import { DropdownListBox } from "./DropdownListBox";

export const MobileDropdown = <TKey extends Key = Key>(props: DropdownProps<TKey>) => {
  const {
    label,
    items,
    placeholder,
    value,
    selectionMode,
    variant,
    customTrigger,
    containerClassName,
    "data-testid": dataTestId,
    isOpen: controlledIsOpen,
    onOpenChange: controlledOnOpenChange,
    useMobileDrawer = false,
  } = props;
  const { t } = useTranslation();

  const selectedItems = items.filter((item) =>
    selectionMode === "multiple" ? value.includes(item.id) : item.id === value,
  );

  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [triggerRef, { width }] = useMeasure();

  // Use controlled state if provided, otherwise use internal state
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const handleOpenChange = (open: boolean) => {
    if (controlledOnOpenChange) {
      controlledOnOpenChange(open);
    } else {
      setInternalIsOpen(open);
    }
  };

  const onChange: DropdownProps<TKey>["onChange"] = (val: typeof value) => {
    if (selectionMode === "multiple") {
      props.onChange(val as TKey[]);
    } else {
      props.onChange(val as TKey);
      handleOpenChange(false);
    }
  };

  const listboxProps = {
    ...props,
    onChange,
  } as DropdownProps<TKey>;

  const triggerButton = customTrigger ?? (
    <DropdownButton
      ref={triggerRef}
      variant={variant}
      data-testid={dataTestId}
    >
      <span className={selectedItems.length > 0 ? "" : "text-secondary-100"}>
        {selectedItems.length > 0 ? selectedItems.map((item) => item.label).join(", ") : placeholder}
      </span>
    </DropdownButton>
  );

  // Render as drawer when useMobileDrawer is true
  if (useMobileDrawer) {
    return (
      <div className={containerClassName}>
        <InputHeader label={label} />
        <Drawer
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          trigger={triggerButton}
        >
          {(close) => (
            <div className="flex h-screen flex-col">
              <DropdownListBox
                header={
                  <div className="px-4 pt-3">
                    <InputHeader
                      label={label}
                      rightContent={
                        <button
                          type="button"
                          onClick={close}
                          aria-label={t("ui.dropdown.closeAlt")}
                          className="shrink-0"
                        >
                          <CloseIcon className="h-6 w-6 text-secondary-50" />
                        </button>
                      }
                    />
                  </div>
                }
                {...listboxProps}
              />
            </div>
          )}
        </Drawer>
      </div>
    );
  }

  // Render as regular popover (default behavior)
  return (
    <div className={containerClassName}>
      <InputHeader label={label} />
      <DialogTrigger
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
      >
        {triggerButton}

        <AriaPopover
          className="my-4 w-[calc(100%-32px)] outline-hidden"
          style={{ width: !customTrigger ? width : undefined }}
          offset={0}
        >
          <Dialog className="dm:w-[calc(100%-32px)]! outline-hidden">
            <div className="rounded-input-rounding bg-secondary-300 shadow-5 outline-hidden">
              <DropdownListBox {...listboxProps} />
            </div>
          </Dialog>
        </AriaPopover>
      </DialogTrigger>
    </div>
  );
};
